/**
 * Browser → Typesense (search-only key). Requires Typesense CORS for your web origin.
 *
 * Config: `typesense-browser-config.ts` and env (Vite injects process.env).
 */

import type { ProductObject } from "app/api/product/product.type";
import { UserType } from "app/api/user/user.types";
import {
  getTypesenseApiKey,
  getTypesenseCollection,
  getTypesenseHost,
  getTypesensePort,
  getTypesenseProtocol,
  getTypesenseNlModelId,
  getTypesenseSearchCacheTtlSeconds,
  getTypesenseSearchUseCache,
} from "app/api/typesense/typesense-browser-config";

export type TypesenseBrowserSearchParams = {
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
  minQty?: string;
  minPrice?: string;
  maxPrice?: string;
  colours?: string[];
  userType?: string;
  normalSearch?: string;
  /** When true, Typesense parses `q` with an LLM (`nl_query=true`) only if `search` has more than 4 words. Requires `nlModelId` or PUBLIC_TYPESENSE_NL_MODEL_ID. */
  nlQuery?: boolean | string;
  /** Overrides PUBLIC_TYPESENSE_NL_MODEL_ID for this request. */
  nlModelId?: string;
  signal?: AbortSignal;
};

export function isTypesenseBrowserConfigured(): boolean {
  return !!(getTypesenseApiKey()?.length && getTypesenseCollection().length);
}

function escapeFilterValue(v: string): string {
  return v.replace(/\\/g, "\\\\").replace(/`/g, "\\`");
}

function getTsPriceFieldNames(userType?: string): { min: string; max: string } {
  const ut = userType || UserType.Non_VIP;
  if (ut === UserType.Gold_VIP) {
    return { min: "user_type_price_gold_vip_min", max: "user_type_price_gold_vip_max" };
  }
  if (ut === UserType.DIAMOND_VIP) {
    return { min: "user_type_price_diamond_vip_min", max: "user_type_price_diamond_vip_max" };
  }
  return { min: "user_type_price_non_vip_min", max: "user_type_price_non_vip_max" };
}

function buildListPriceFilterFragment(
  pm: string,
  pM: string,
  minPriceValue?: number,
  maxPriceValue?: number,
): string | null {
  const hasMin = typeof minPriceValue === "number" && !Number.isNaN(minPriceValue);
  const hasMax = typeof maxPriceValue === "number" && !Number.isNaN(maxPriceValue);

  if (hasMin && hasMax) {
    return `(${pm}:<=${maxPriceValue} && ${pM}:>=${minPriceValue})`;
  }

  const parts: string[] = [];
  if (hasMin) {
    const P = minPriceValue as number;
    parts.push(`(${pm}:<=${P} && ${pM}:>=${P})`, `(${pm}:<=${P} && ${pM}:=0)`);
  }
  if (hasMax) {
    const M = maxPriceValue as number;
    parts.push(`(${pm}:<=${M} && ${pM}:>=${M})`, `(${pm}:<=${M} && ${pM}:=0)`);
  }
  if (parts.length === 0) return null;
  return `(${parts.join(" || ")})`;
}

function countTrimmedWords(text: string | undefined): number {
  const t = text?.trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

function typesenseNlFilterErrorInBody(text: string): boolean {
  return /Error with filter|Not a float|filter field/i.test(text);
}

function typesenseNlFilterErrorMessage(message: string): boolean {
  return /Error with filter|Not a float/i.test(message);
}

function mapDocumentToProductObject(doc: Record<string, unknown>): ProductObject {
  const id = String(doc.id ?? "");
  const slug = String(doc.slug ?? "");
  const labels = Array.isArray(doc.labels) ? (doc.labels as string[]) : [];
  const min = Number(doc.user_type_price_non_vip_min ?? 0);
  const max = Number(doc.user_type_price_non_vip_max ?? 0);

  const out: ProductObject = {
    _id: id,
    slug,
    meta: {} as ProductObject["meta"],
    __v: 0,
    category: {
      _id: "",
      name: String(doc.supplier_category ?? ""),
      supplier: "",
      __v: 0,
      isActive: true,
      status: "",
      totalProducts: 0,
    },
    subCategory: {
      _id: "",
      name: String(doc.supplier_sub_category ?? ""),
      category: "",
      __v: 0,
      isActive: true,
      status: "",
    },
    globalProductCategory: {
      name: String(doc.supplier_category ?? ""),
    },
    globalProductSubCategory: {
      name: String(doc.supplier_sub_category ?? ""),
    },
    labels,
    createdAt: "",
    updatedAt: "",
    overview: {
      name: String(doc.overview_name ?? ""),
      code: String(doc.overview_code ?? ""),
      heroImage: String(doc.overview_hero_image ?? ""),
      minQty: Number(doc.quantity_min ?? 0),
      supplier: "",
      displayPrices: {},
    },
    product: {
      code: String(doc.product_code ?? ""),
      name: String(doc.product_name ?? ""),
      description: String(doc.product_description ?? ""),
      discontinued: Boolean(doc.product_discontinued),
      supplierBrand: String(doc.supplier_brand ?? ""),
      supplierLabel: String(doc.supplier_label ?? ""),
      supplierCatalogue: String(doc.supplier_catalogue ?? ""),
      supplierWebsitePage: String(doc.supplier_website_page ?? ""),
      images: Array.isArray(doc.product_images) ? (doc.product_images as string[]) : [],
      prices: {} as ProductObject["product"]["prices"],
      videos: [],
      lineArt: [],
      colours: { list: [], supplierText: [] },
      categorisation: {} as ProductObject["product"]["categorisation"],
      details: [],
    },
    supplier: {} as ProductObject["supplier"],
    isActive: true,
    isHot: false,
    price: { min, max },
    userTypePrice: {
      [UserType.Non_VIP]: {
        min: Number(doc.user_type_price_non_vip_min ?? 0),
        max: Number(doc.user_type_price_non_vip_max ?? 0),
      },
      [UserType.Gold_VIP]: {
        min: Number(doc.user_type_price_gold_vip_min ?? 0),
        max: Number(doc.user_type_price_gold_vip_max ?? 0),
      },
      [UserType.DIAMOND_VIP]: {
        min: Number(doc.user_type_price_diamond_vip_min ?? 0),
        max: Number(doc.user_type_price_diamond_vip_max ?? 0),
      },
    },
    quantity: {
      min: Number(doc.quantity_min ?? 0),
      max: Number(doc.quantity_max ?? 0),
    },
    status: "",
    baseprices: [],
  };
  return out;
}

export async function searchTypesenseBrowser(params: TypesenseBrowserSearchParams): Promise<{
  products: ProductObject[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}> {
  const host = getTypesenseHost();
  const apiKey = getTypesenseApiKey();
  const collection = getTypesenseCollection();
  if (!apiKey) {
    throw new Error("Typesense browser env not configured (PUBLIC_TYPESENSE_API_KEY)");
  }

  const page = Number(params.page) || 1;
  const limit = Math.min(Number(params.limit) || 20, 1000);

  const { min: priceMinField, max: priceMaxField } = getTsPriceFieldNames(params.userType);

  const sortMap: Record<string, string> = {
    "A-Z": "overview_name:asc",
    "Z-A": "overview_name:desc",
    "recently added": "meta_first_listed_at:desc",
    "lowest-price": `${priceMinField}:asc`,
    "highest-price": `${priceMaxField}:desc`,
    default: `${priceMinField}:asc`,
  };
  const sortBy = sortMap[params.sort || ""] || sortMap.default;

  const filters: string[] = [
    "is_searchable:=true",
    "overview_hero_image:!=\"\"",
    "overview_name:!=\"\"",
    "overview_code:!=\"\"",
    "product_description:!=\"\"",
    "product_discontinued:=false",
    "user_type_price_non_vip_min:>0",
    "quantity_min:>1",
  ];

  const minPriceStr =
    params.minPrice !== undefined && params.minPrice !== null ? String(params.minPrice).trim() : "";
  const maxPriceStr =
    params.maxPrice !== undefined && params.maxPrice !== null ? String(params.maxPrice).trim() : "";
  const minPriceValue = minPriceStr !== "" ? Number(minPriceStr) : undefined;
  const maxPriceValue = maxPriceStr !== "" ? Number(maxPriceStr) : undefined;
  const hasMinPrice = typeof minPriceValue === "number" && !Number.isNaN(minPriceValue);
  const hasMaxPrice = typeof maxPriceValue === "number" && !Number.isNaN(maxPriceValue);

  const priceFilter = buildListPriceFilterFragment(
    priceMinField,
    priceMaxField,
    hasMinPrice ? minPriceValue : undefined,
    hasMaxPrice ? maxPriceValue : undefined,
  );
  if (priceFilter) filters.push(priceFilter);

  if (params.minQty != null && params.minQty !== "" && !Number.isNaN(Number(params.minQty))) {
    const R = Number(params.minQty);
    filters.push(`quantity_min:<=${R}`);
    filters.push(`quantity_max:>=${R}`);
  }

  if (params.colours?.length) {
    for (const c of params.colours) {
      const t = c?.trim();
      if (t) filters.push(`colours:=${escapeFilterValue(t.toLowerCase())}`);
    }
  }

  const filterBy = filters.join(" && ");
  const q = params.search?.trim() ? params.search.trim() : "*";

  const queryBy =
    "overview_name,overview_code,keywords,product_description";
  const queryByWeights = "4,3,2,1";

  /** Matches server `normalSearch === 'true'`: overview + routing fields only (smaller Typesense payload). */
  const overviewOnlyIncludeFields =
    "slug,overview_name,overview_code,overview_hero_image,supplier_category,supplier_sub_category,is_searchable,colours";

  const port = getTypesensePort();
  const protocol = getTypesenseProtocol();
  const baseUrl = `${protocol}://${host}:${port}`;

  const nlModelId = params.nlModelId?.trim() || getTypesenseNlModelId();
  const nlEnabled =
    params.nlQuery === true ||
    params.nlQuery === "true" ||
    (typeof params.nlQuery === "string" && params.nlQuery.toLowerCase() === "true");
  const shouldTryNl =
    Boolean(nlEnabled && nlModelId) && countTrimmedWords(params.search) > 4;

  const buildSearchUrl = (withNl: boolean): URL => {
    const url = new URL(`${baseUrl}/collections/${encodeURIComponent(collection)}/documents/search`);
    url.searchParams.set("q", q);
    url.searchParams.set("query_by", queryBy);
    url.searchParams.set("query_by_weights", queryByWeights);
    url.searchParams.set("filter_by", filterBy);
    url.searchParams.set("sort_by", sortBy);
    url.searchParams.set("page", String(page));
    url.searchParams.set("per_page", String(limit));
    url.searchParams.set("highlight_fields", "overview_name");
    url.searchParams.set("num_typos", "2");
    url.searchParams.set("prefix", "true");
    if (withNl && shouldTryNl && nlModelId) {
      url.searchParams.set("nl_query", "true");
      url.searchParams.set("nl_model_id", nlModelId);
    }
    if (params.normalSearch === "true") {
      url.searchParams.set("include_fields", overviewOnlyIncludeFields);
    }
    if (getTypesenseSearchUseCache()) {
      url.searchParams.set("use_cache", "true");
      url.searchParams.set("cache_ttl", String(getTypesenseSearchCacheTtlSeconds()));
    }
    return url;
  };

  const fetchSearch = (withNl: boolean) =>
    fetch(buildSearchUrl(withNl).toString(), {
      method: "GET",
      headers: { "X-TYPESENSE-API-KEY": apiKey },
      signal: params.signal,
    });

  let res = await fetchSearch(shouldTryNl);

  if (!res.ok && shouldTryNl) {
    const text = await res.text();
    if (typesenseNlFilterErrorInBody(text)) {
      res = await fetchSearch(false);
    } else {
      throw new Error(`Typesense search failed: ${res.status} ${text}`);
    }
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Typesense search failed: ${res.status} ${text}`);
  }

  type TsSearchJson = {
    hits?: { document?: Record<string, unknown> }[];
    found?: number;
    message?: string;
  };

  let json = (await res.json()) as TsSearchJson;

  if (
    shouldTryNl &&
    typeof json.message === "string" &&
    typesenseNlFilterErrorMessage(json.message)
  ) {
    res = await fetchSearch(false);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Typesense search failed: ${res.status} ${text}`);
    }
    json = (await res.json()) as TsSearchJson;
  }

  const hits = json.hits || [];
  const total = json.found ?? 0;
  const products = hits
    .map((h) => h.document)
    .filter(Boolean)
    .map((d) => mapDocumentToProductObject(d as Record<string, unknown>));

  const totalPages = Math.ceil(total / limit) || 0;
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  return {
    products,
    pagination: {
      total,
      page,
      limit,
      totalPages,
      hasNextPage,
      hasPrevPage,
      nextPage: hasNextPage ? page + 1 : null,
      prevPage: hasPrevPage ? page - 1 : null,
    },
  };
}
